interface PendingRegistration {
  eventId: string;
  eventName: string | null;
  countryCode: string;
  stateCode: string | null;
  checkoutState: (Record<string, unknown> & { lastReachedStep?: unknown }) | null;
  minimumDonation: string | null;
}

interface RegistrationStatus {
  blocked: boolean;
  pendingRegistration: PendingRegistration | null;
}

interface AuthenticatedProfile {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
}

interface ResolveRegistrationInitStateInput {
  isHydrating: boolean;
  authUser: { email: string } | null;
  entrant: { id: string } | null;
  registrationStatus: RegistrationStatus | null;
  isRedirectReturn: boolean;
  urlIntent: string | null;
}

export interface RegistrationInitState {
  isLoading: boolean;
  initialStep: number;
  isRedirectReturn: boolean;
  isBlocked: boolean;
  pendingRegistration: PendingRegistration | null;
  shouldSkipAuthStep: boolean;
  authenticatedProfile: AuthenticatedProfile | null;
}

export function resolveRegistrationInitState(
  input: ResolveRegistrationInitStateInput,
): RegistrationInitState {
  const { isHydrating, authUser, entrant, registrationStatus, isRedirectReturn, urlIntent } = input;

  if (isHydrating) {
    return {
      isLoading: true,
      initialStep: 1,
      isRedirectReturn,
      isBlocked: false,
      pendingRegistration: null,
      shouldSkipAuthStep: false,
      authenticatedProfile: null,
    };
  }

  if (!authUser) {
    return {
      isLoading: false,
      initialStep: 1,
      isRedirectReturn,
      isBlocked: false,
      pendingRegistration: null,
      shouldSkipAuthStep: false,
      authenticatedProfile: null,
    };
  }

  if (!entrant) {
    return {
      isLoading: false,
      initialStep: 1,
      isRedirectReturn: false,
      isBlocked: false,
      pendingRegistration: null,
      shouldSkipAuthStep: false,
      authenticatedProfile: {
        email: authUser.email,
        firstName: '',
        lastName: '',
        phone: null,
      },
    };
  }

  if (!registrationStatus) {
    return {
      isLoading: true,
      initialStep: 1,
      isRedirectReturn,
      isBlocked: false,
      pendingRegistration: null,
      shouldSkipAuthStep: true,
      authenticatedProfile: null,
    };
  }

  if (registrationStatus.blocked && !isRedirectReturn) {
    return {
      isLoading: false,
      initialStep: 1,
      isRedirectReturn,
      isBlocked: true,
      pendingRegistration: null,
      shouldSkipAuthStep: true,
      authenticatedProfile: null,
    };
  }

  if (isRedirectReturn) {
    return {
      isLoading: false,
      initialStep: 4,
      isRedirectReturn: true,
      isBlocked: false,
      pendingRegistration: registrationStatus.pendingRegistration,
      shouldSkipAuthStep: true,
      authenticatedProfile: null,
    };
  }

  if (urlIntent === 'changeRide') {
    return {
      isLoading: false,
      initialStep: 1,
      isRedirectReturn: false,
      isBlocked: false,
      pendingRegistration: registrationStatus.pendingRegistration,
      shouldSkipAuthStep: true,
      authenticatedProfile: null,
    };
  }

  if (registrationStatus.pendingRegistration?.eventId) {
    const lastReachedStep = registrationStatus.pendingRegistration.checkoutState?.lastReachedStep;

    return {
      isLoading: false,
      initialStep: lastReachedStep === 4 ? 4 : 3,
      isRedirectReturn: false,
      isBlocked: false,
      pendingRegistration: registrationStatus.pendingRegistration,
      shouldSkipAuthStep: true,
      authenticatedProfile: null,
    };
  }

  return {
    isLoading: false,
    initialStep: 1,
    isRedirectReturn: false,
    isBlocked: false,
    pendingRegistration: null,
    shouldSkipAuthStep: true,
    authenticatedProfile: null,
  };
}
